import {
  axisFor,
  buildOverlay,
  describeExclusion,
  type Aggregate,
  type BucketSize,
  type Entry,
  type Track,
} from '@anytracker/core'
import { useMemo, useState } from 'react'

import { Button } from '../components/Button.js'
import { formatDate } from '../format/dates.js'
import { ChartFrame, type SideAxis } from './ChartFrame.js'
import styles from './Charts.module.css'
import { seriesDash, trackColour } from './colours.js'
import { linePath, xOf, yOf, type Span } from './geometry.js'

interface OverlayChartProps {
  readonly tracks: readonly Track[]
  readonly entries: readonly Entry[]
  readonly span: Span
  readonly size: BucketSize
  readonly aggregate: Aggregate
  readonly deviceZone: string
}

/** A normalised axis has no units; labelling it 0–1 would invite reading it as one. */
const NORMALISED_TICKS = [
  { quantity: 0, label: 'low' },
  { quantity: 0.5, label: '' },
  { quantity: 1, label: 'high' },
]

/**
 * Several tracks on one time axis (T-404).
 *
 * The chart that answers "did these move together", and the easiest one to
 * mislead with — two differently-scaled series in one rectangle invite reading
 * their heights as comparable magnitudes. Three things push back:
 *
 *  - The y-axis is labelled `low`/`high`, not with numbers. There is no shared
 *    scale, so there is no number that could honestly go there.
 *  - A note says so in words, above the chart rather than in a footnote.
 *  - Every tooltip and every row of the table shows the **original** value in
 *    the track's own units. The 0–1 figure decides a height and is never shown.
 *
 * No correlation coefficient, deliberately. See the ticket: a number computed
 * across irregularly sampled self-reported series, printed in a document
 * someone makes decisions from, invites a conclusion the data cannot support.
 */
export function OverlayChart({
  tracks,
  entries,
  span,
  size,
  aggregate,
  deviceZone,
}: OverlayChartProps) {
  const [focused, setFocused] = useState<string | null>(null)
  const [ownScales, setOwnScales] = useState(false)

  const overlay = useMemo(
    () =>
      buildOverlay(
        tracks.map((track) => ({ track, entries })),
        { size, aggregate, range: { fromEpochMs: span.from, toEpochMs: span.to } },
      ),
    [tracks, entries, size, aggregate, span.from, span.to],
  )

  const timeTicks = useMemo(() => {
    const middle = span.from + (span.to - span.from) / 2
    return [span.from, middle, span.to].map((epochMs) => ({
      epochMs,
      label: formatDate({ epochMs, timeZone: deviceZone }),
    }))
  }, [span.from, span.to, deviceZone])

  const withData = overlay.series.filter((series) => series.points.length > 0)

  /** Every period any track has data in, so a shared crosshair has rows to show. */
  const columns = useMemo(() => {
    return overlay.axis.map((slot) => ({
      ...slot,
      readings: overlay.series
        .map((series) => ({
          track: series.track,
          point: series.points.find((point) => point.bucketKey === slot.key),
        }))
        .filter(
          (
            reading,
          ): reading is { track: Track; point: NonNullable<typeof reading.point> } =>
            reading.point !== undefined,
        ),
    }))
  }, [overlay])

  const chosen = columns.find((column) => column.key === focused)

  /**
   * Real ticks for exactly two tracks.
   *
   * Because normalisation is against each track's *declared* range, the points
   * are already positioned exactly where dual axes would put them — so this is
   * purely a change of labels, not of geometry. Above two tracks it is not
   * offered: a reader cannot tell which of three lines belongs to which of two
   * axes, and a chart nobody can attribute is worse than one that admits it has
   * no shared scale.
   */
  const leftTrack = withData[0]?.track
  const rightTrack = withData[1]?.track
  const leftOwn = leftTrack === undefined ? null : axisFor(leftTrack)
  const rightOwn = rightTrack === undefined ? null : axisFor(rightTrack)
  const dualAxis =
    withData.length === 2 && ownScales && leftOwn !== null && rightOwn !== null

  const rightAxis: SideAxis | undefined =
    dualAxis && rightOwn !== null && rightTrack !== undefined
      ? {
          range: { min: rightOwn.min, max: rightOwn.max },
          ticks: rightOwn.ticks,
          colour: trackColour(rightTrack),
        }
      : undefined

  return (
    <section className={styles['card']}>
      <div className={styles['head']}>
        <h2 className={styles['title']}>Together</h2>
        <span className={styles['kindNote']}>
          {aggregate} per {size}
        </span>
      </div>

      {dualAxis ? (
        <p className={styles['normalisedNote']}>
          Each track has its own axis — {leftTrack?.name ?? ''} on the left,{' '}
          {rightTrack?.name ?? ''} on the right.
        </p>
      ) : (
        <p className={styles['normalisedNote']}>
          Each track is drawn against its own scale, so the lines can be compared for
          shape but not for height. Every value shown in a tooltip or the table below is
          the real one.
        </p>
      )}

      {withData.length === 2 && (
        <Button
          aria-pressed={ownScales}
          onClick={() => {
            setOwnScales(!ownScales)
          }}
        >
          {ownScales ? 'Use one axis' : 'Show each scale'}
        </Button>
      )}

      {withData.length === 0 ? (
        <p className={styles['nothing']}>
          Nothing recorded for these tracks in this range.
        </p>
      ) : (
        <ChartFrame
          label={`${withData.map((series) => series.track.name).join(', ')} over time, each on its own scale.`}
          valueRange={
            dualAxis && leftOwn !== null
              ? { min: leftOwn.min, max: leftOwn.max }
              : { min: 0, max: 1 }
          }
          valueTicks={dualAxis && leftOwn !== null ? leftOwn.ticks : NORMALISED_TICKS}
          {...(rightAxis === undefined ? {} : { rightAxis })}
          {...(dualAxis && leftTrack !== undefined
            ? { leftColour: trackColour(leftTrack) }
            : {})}
          span={span}
          timeTicks={timeTicks}
        >
          {withData.map((series, index) => {
            const colour = trackColour(series.track)
            const dash = seriesDash(index)
            const points = series.points.map((point) => ({
              x: xOf(point.startEpochMs, span),
              y: yOf(point.normalised, { min: 0, max: 1 }),
              followsGap: point.followsGap,
              key: point.bucketKey,
              label: point.label,
            }))
            return (
              <g key={series.track.id}>
                {/*
                  Dashed as well as coloured. These reports get printed on
                  office printers, where eight distinct hues become eight
                  indistinguishable greys — so the pattern is what actually
                  separates the lines, and the colour is a convenience (T-504).
                */}
                <path
                  d={linePath(points)}
                  fill="none"
                  stroke={colour}
                  {...(dash === undefined ? {} : { strokeDasharray: dash })}
                  className={styles['line']}
                />
                {points.map((point) => (
                  <circle
                    key={point.key}
                    cx={point.x}
                    cy={point.y}
                    r={focused === point.key ? 5 : 3}
                    fill={colour}
                    className={styles['point']}
                    onClick={() => {
                      setFocused(focused === point.key ? null : point.key)
                    }}
                  >
                    <title>{`${series.track.name}: ${point.label}`}</title>
                  </circle>
                ))}
              </g>
            )
          })}
        </ChartFrame>
      )}

      <ul className={styles['legend']}>
        {withData.map((series, index) => (
          <li key={series.track.id}>
            {/*
              A line sample rather than a block of colour, so the legend carries
              the dash pattern too. In greyscale the swatch is the only thing
              that can tell two series apart, and a solid square cannot.
            */}
            <svg
              className={styles['swatchLine']}
              viewBox="0 0 24 8"
              width="24"
              height="8"
              aria-hidden="true"
            >
              <path
                d="M0 4 H24"
                fill="none"
                stroke={trackColour(series.track)}
                strokeWidth="2"
                {...(seriesDash(index) === undefined
                  ? {}
                  : { strokeDasharray: seriesDash(index) })}
              />
            </svg>
            {series.track.name}
            <span className={styles['legendScale']}>
              {series.range.min}–{series.range.max}
            </span>
          </li>
        ))}
      </ul>

      {overlay.excluded.length > 0 && (
        <ul className={styles['excluded']}>
          {overlay.excluded.map((excluded) => (
            <li key={excluded.track.id}>{describeExclusion(excluded)}</li>
          ))}
        </ul>
      )}

      {chosen !== undefined && (
        <div className={styles['selection']} data-testid="overlay-selection">
          <div className={styles['selectionHead']}>
            <strong>{chosen.key}</strong>
          </div>
          <ul className={styles['selectionList']}>
            {chosen.readings.map((reading) => (
              <li key={reading.track.id}>
                <span>
                  <span
                    className={styles['swatch']}
                    style={{ background: trackColour(reading.track) }}
                  />
                  {reading.track.name}
                </span>
                {/* The recorded value, never the 0–1 one used to place the dot. */}
                <strong>{reading.point.label}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        The same fallback the single-track chart has, and needed for the same
        reason: this drawing is `role="img"`, so a screen-reader user has no
        other route to the values. Every cell is the value as recorded — the
        normalised figure exists only to decide a height and appears nowhere.
      */}
      {withData.length > 0 && (
        <details className={styles['tableWrap']}>
          <summary>Show the numbers</summary>
          <table className={styles['table']}>
            <thead>
              <tr>
                <th scope="col">Period</th>
                {withData.map((series) => (
                  <th key={series.track.id} scope="col">
                    {series.track.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {columns.map((column) => (
                <tr key={column.key}>
                  <th scope="row">{column.key}</th>
                  {withData.map((series) => {
                    const reading = column.readings.find(
                      (found) => found.track.id === series.track.id,
                    )
                    return (
                      <td key={series.track.id}>
                        {/* An empty cell, not a zero: this track was not
                            measured in this period. */}
                        {reading?.point.label ?? ''}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  )
}
